/**
 * 카드뉴스 - 익명 식별자 · 이벤트 전송 · 접근 권한(서명 쿠키) 획득
 *
 * 퍼널:  발송 → LANDING → VIEW → PROGRESS → CLICK
 *   LANDING  카톡에서 링크를 눌러 도착한 순간. 로그인 이전이라 비인증으로 보낸다.
 *   VIEW     인증을 통과해 카드뉴스가 실제로 열린 순간.
 *   PROGRESS 이탈 직전 1건만. 최대 도달 슬라이드와 체류시간을 담는다.
 *   CLICK    슬라이드 위 버튼을 누른 순간.
 */
import Cookies from "js-cookie";
import api from "@/api";

const ANON_KEY = "zpt_anon_id";
const ANON_COOKIE_DAYS = 730;
const EVENTS_PATH = "/public/cardnews/events";

/**
 * 브라우저별 익명 식별자.
 * 카카오톡 인앱 웹뷰는 저장소가 비워지는 경우가 있어 localStorage 와 쿠키를 함께 쓴다.
 */
export function getAnonId() {
  let id = null;
  try {
    id = window.localStorage.getItem(ANON_KEY);
  } catch (e) {
    /* 프라이빗 모드 등에서 접근이 막힐 수 있다 */
  }
  if (!id) id = Cookies.get(ANON_KEY);
  if (!id) id = uuid();

  try {
    window.localStorage.setItem(ANON_KEY, id);
  } catch (e) {
    /* 무시 - 쿠키로 유지된다 */
  }
  Cookies.set(ANON_KEY, id, { expires: ANON_COOKIE_DAYS, sameSite: "Lax" });
  return id;
}

function uuid() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function eventsUrl() {
  return (api.defaults.baseURL || "").replace(/\/$/, "") + EVENTS_PATH;
}

/**
 * 토큰은 store/mutations 가 VUE_APP_CookieToken 쿠키에 보관한다.
 * axios(api)는 로그인 시 defaults 에 토큰을 이미 넣으므로 axios 호출에는 붙이지 않는다.
 * 중복해서 붙이면 헤더가 콤마로 합쳐지고("Bearer a, Bearer a"),
 * 서버의 resolveToken 이 substring(7) 만 하므로 검증이 깨져 401 이 난다.
 * 아래 fetch 계열에만 사용한다.
 */
function authHeader() {
  const token = Cookies.get(process.env.VUE_APP_CookieToken);
  return token ? { Authorization: "Bearer " + token } : {};
}

/**
 * 카드뉴스 접근 요청은 반드시 chatzpt.co.kr 하위 호스트로 보내야 한다.
 * 응답으로 내려오는 CloudFront 서명 쿠키가 Domain=.chatzpt.co.kr 인데,
 * 브라우저는 응답한 호스트가 커버하지 않는 도메인의 쿠키를 거부한다.
 * was.philipk.im 으로 보내면 쿠키가 저장되지 않아 CDN 요청이 전부 403 이 된다.
 */
function accessBase() {
  if (typeof window !== "undefined" &&
      /(^|\.)chatzpt\.co\.kr$/.test(window.location.hostname)) {
    return "https://api.chatzpt.co.kr";
  }
  return (api.defaults.baseURL || "").replace(/\/$/, "");
}

/**
 * 이벤트 전송. 화면 전환을 막지 않도록 절대 await 를 강제하지 않는다.
 * 집계 실패가 사용자 흐름에 영향을 주면 안 된다.
 */
export function sendEvent(payload) {
  const body = JSON.stringify({ anonId: getAnonId(), ...payload });
  return fetch(eventsUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body,
    keepalive: true,
    credentials: "omit",
  }).catch(() => {
    /* 무시 */
  });
}

/**
 * 이탈 시점 전송용. 페이지가 사라지는 중이라 fetch 가 취소될 수 있어 sendBeacon 을 쓴다.
 * sendBeacon 은 헤더를 못 붙이므로 회원 식별은 anonId 소급 연결에 의존한다.
 */
export function sendBeaconEvent(payload) {
  const body = JSON.stringify({ anonId: getAnonId(), ...payload });
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(eventsUrl(), blob)) return;
  }
  sendEvent(payload);
}

/**
 * 카드뉴스 접근 권한 획득.
 * 인증을 확인받고 CloudFront 서명 쿠키를 발급받는다.
 * 이후 manifest 와 이미지는 CDN 에서 직접 읽는다(WAS 를 거치지 않음).
 */
export async function requestAccess(slug) {
  // headers 를 직접 넘기지 않는다 - axios defaults 에 토큰이 이미 있어 중복되면 401 이 난다.
  const res = await api.get(
    `${accessBase()}/user/cardnews/${encodeURIComponent(slug)}/access`,
    { withCredentials: true }
  );
  return res.data;
}

/** manifest 는 CDN 에서 직접. 서명 쿠키가 실려야 하므로 credentials 를 포함한다. */
export async function loadManifest(manifestUrl) {
  const res = await fetch(manifestUrl, { credentials: "include" });
  if (!res.ok) throw new Error("manifest 로드 실패: " + res.status);
  return res.json();
}

/**
 * 로그인 후 돌아갈 경로 검증.
 * 오픈 리다이렉트를 막기 위해 내부 경로만, 그중에서도 카드뉴스만 허용한다.
 */
export function safeReturnTo(value) {
  if (!value || typeof value !== "string") return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  if (!/^\/friends\/[A-Za-z0-9_-]{3,60}\/?$/.test(value)) return null;
  return value;
}
